import { UserIdentityStatusEnum } from "@/components/types/UserIdentity.Status";
import type { Identity, IdentityPersonType, IdentityRole, SuitabilityTestStatus } from "@/screen/Identities/Interfaces/IdentitiesMainScreenInterfaces";
import type { FormMode } from "@/screen/Identities/Interfaces/IdentityFormInterfaces";
import type { MainUserIdentityDto } from "@/services/UserIdentitiesService";

const SuitabilityTestStatusValues: readonly SuitabilityTestStatus[] = ["NOT_APPLICABLE", "IN_FORCE", "EXPIRED", "NONE"];
const PersonTypeValues: readonly IdentityPersonType[] = ["NaturalPerson", "LegalEntity", "LegalRepresentative"];

function readField(obj: Record<string, unknown>, camelKey: string, pascalKey: string): unknown
{
    if(obj[camelKey] !== undefined)
    {
        return obj[camelKey];
    }
    return obj[pascalKey];
}

/** Solo los cuatro estados del contrato. Cualquier otro valor se trata como ausente. */
function readSuitabilityTestStatus(value: unknown): SuitabilityTestStatus | undefined
{
    if(typeof value !== "string")
    {
        return undefined;
    }

    const normalized = value.trim();
    return SuitabilityTestStatusValues.find((status) => status === normalized);
}

/** Solo las tres tipologías del backend. Cualquier otro valor se trata como ausente (persona física). */
function readPersonType(value: unknown): IdentityPersonType | undefined
{
    if(typeof value !== "string")
    {
        return undefined;
    }

    const normalized = value.trim();
    return PersonTypeValues.find((personType) => personType === normalized);
}

function readOptionalBoolean(value: unknown): boolean | undefined
{
    if(typeof value === "boolean")
    {
        return value;
    }

    return undefined;
}

function readOptionalDate(value: unknown): string | undefined
{
    if(typeof value === "string" && value.trim() !== "")
    {
        return value;
    }

    if(value instanceof Date && !Number.isNaN(value.getTime()))
    {
        return value.toISOString();
    }

    return undefined;
}

export function normalizeIdentityStatus(status: string | null | undefined): string
{
    return String(status ?? "").trim().toUpperCase();
}

/** Borrador del titular creado al pagar el plan: hay que editarlo, no dar de alta otro. */
export function isMainNewDraft(status: string | null | undefined): boolean
{
    return normalizeIdentityStatus(status) === UserIdentityStatusEnum.New;
}

/** El paso 2.1 solo se cierra con el Main del titular en estos estados. */
export function isMainSelectableForStep21(status: string | null | undefined): boolean
{
    const normalized = normalizeIdentityStatus(status);
    return normalized === UserIdentityStatusEnum.Pending
        || normalized === UserIdentityStatusEnum.Active
        || normalized === UserIdentityStatusEnum.Outdated;
}

export function normalizeMainUserIdentityDto(raw: unknown): MainUserIdentityDto | null
{
    if(!raw || typeof raw !== "object")
    {
        return null;
    }
    const record = raw as Record<string, unknown>;
    const id = Number(readField(record, "id", "Id") ?? 0);
    if(!Number.isFinite(id) || id <= 0)
    {
        return null;
    }
    return{
        id: id,
        firstName: String(readField(record, "firstName", "FirstName") ?? ""),
        lastName: String(readField(record, "lastName", "LastName") ?? ""),
        secondLastName: String(readField(record, "secondLastName", "SecondLastName") ?? ""),
        status: String(readField(record, "status", "Status") ?? ""),
    };
}

export function normalizeIdentityListItem(raw: unknown): Identity | null
{
    if(!raw || typeof raw !== "object")
    {
        return null;
    }
    const record = raw as Record<string, unknown>;
    const id = Number(readField(record, "id", "Id") ?? 0);
    if(!Number.isFinite(id) || id <= 0)
    {
        return null;
    }
    const isMain = Boolean(readField(record, "isMain", "IsMain") ?? readField(record, "main", "Main"));
    const roleRaw = readField(record, "role", "Role");
    const role: IdentityRole = roleRaw === "Guest" ? "Guest" : "Owner";
    const savingsPlansRaw = readField(record, "savingsPlans", "SavingsPlans");
    const associatedUsersRaw = readField(record, "associatedUsers", "AssociatedUsers");
    const canEditRaw = readField(record, "canEdit", "CanEdit");

    return{
        id: id,
        firstName: String(readField(record, "firstName", "FirstName") ?? ""),
        lastName: String(readField(record, "lastName", "LastName") ?? ""),
        secondLastName: String(readField(record, "secondLastName", "SecondLastName") ?? ""),
        fullName: typeof readField(record, "fullName", "FullName") === "string"
            ? String(readField(record, "fullName", "FullName"))
            : undefined,
        email: String(readField(record, "email", "Email") ?? ""),
        isMain: isMain,
        isMinor: Boolean(readField(record, "isMinor", "IsMinor")),
        personType: readPersonType(readField(record, "personType", "PersonType")),
        status: readField(record, "status", "Status") as string | undefined,
        kycStatus: readField(record, "kycStatus", "KycStatus") as string | undefined,
        kycCompleted: Boolean(readField(record, "kycCompleted", "KycCompleted")),
        kycDataComplete: readField(record, "kycDataComplete", "KycDataComplete") as boolean | undefined,
        suitabilityTestCompleted: readField(record, "suitabilityTestCompleted", "SuitabilityTestCompleted") as boolean | undefined,
        suitabilityTestStatus: readSuitabilityTestStatus(readField(record, "suitabilityTestStatus", "SuitabilityTestStatus")),
        canUpdateSuitabilityTest: readOptionalBoolean(readField(record, "canUpdateSuitabilityTest", "CanUpdateSuitabilityTest")),
        suitabilityTestNextUpdateAt: readOptionalDate(readField(record, "suitabilityTestNextUpdateAt", "SuitabilityTestNextUpdateAt")),
        role: role,
        canEdit: canEditRaw === undefined ? true : Boolean(canEditRaw),
        canDelete: isMain ? false : Boolean(readField(record, "canDelete", "CanDelete")),
        canRenounce: Boolean(readField(record, "canRenounce", "CanRenounce")),
        savingsPlans: Array.isArray(savingsPlansRaw) ? savingsPlansRaw as Identity["savingsPlans"] : [],
        associatedUsers: Array.isArray(associatedUsersRaw) ? associatedUsersRaw as Identity["associatedUsers"] : [],
    };
}

export function normalizeIdentityListItems(raw: unknown): Identity[]
{
    if(!Array.isArray(raw))
    {
        return [];
    }
    return raw.map(normalizeIdentityListItem).filter((item): item is Identity => item !== null);
}

/**
 * El listado 2.1 es por usuario, no por plan. Si byUser no trae el Main (aún no hay links de 2.2),
 * se inserta a partir de GetMainIdentityByUserId. Nunca se promociona un cotitular a titular.
 */
export function mergeMainIntoIdentitiesList(identities: Identity[], main: MainUserIdentityDto | null): Identity[]
{
    if(!main || !(main.id > 0))
    {
        return identities;
    }

    const index = identities.findIndex((identity) => identity.id === main.id);
    if(index >= 0)
    {
        const existing = identities[index];
        const next = identities.slice();
        next[index] =
        {
            ...existing,
            isMain: true,
            status: existing.status || main.status,
            firstName: existing.firstName || main.firstName,
            lastName: existing.lastName || main.lastName,
            secondLastName: existing.secondLastName || main.secondLastName,
            canDelete: false,
        };
        return next;
    }

    const placeholder: Identity =
    {
        id: main.id,
        firstName: main.firstName ?? "",
        lastName: main.lastName ?? "",
        secondLastName: main.secondLastName ?? "",
        email: "",
        isMain: true,
        isMinor: false,
        status: main.status,
        kycCompleted: false,
        role: "Owner",
        canEdit: true,
        canDelete: false,
        canRenounce: false,
        savingsPlans: [],
        associatedUsers: [],
    };
    return [placeholder, ...identities];
}

/**
 * Id de la ruta PATCH saveUserIdentity/{userIdentityId}.
 * El JSON no manda: 0 es alta de cotitular; el id real edita esa fila.
 * Si hay un único borrador Main NEW, ese id gana sobre el 0 del alta.
 * No se usa un cotitular relleno como titular (casos ya rotos).
 */
export function resolveSaveUserIdentityDraftId(formId: number | null | undefined, routeUserIdentityId: number | null | undefined, mainNewDraftId: number | null | undefined): number
{
    const fromForm = Number(formId ?? 0);
    if(fromForm > 0)
    {
        return fromForm;
    }

    const fromRoute = Number(routeUserIdentityId ?? 0);
    if(fromRoute > 0)
    {
        return fromRoute;
    }

    const fromMainNew = Number(mainNewDraftId ?? 0);
    if(fromMainNew > 0)
    {
        return fromMainNew;
    }

    return 0;
}

/** En alta, si el titular es el borrador NEW del pago, hay que editarlo en lugar de crear otra fila. */
export function resolveCreateRedirectToMainDraftId(mode: FormMode, main: MainUserIdentityDto | null): number | null
{
    if(mode === "edit")
    {
        return null;
    }
    if(!main || !(main.id > 0) || !isMainNewDraft(main.status))
    {
        return null;
    }
    return main.id;
}

/**
 * Principal vacío de nacimiento: Main, en NEW y sin DNI guardado. Es el único principal que el usuario puede
 * sustituir por un interviniente recibido por cesión. El backend lo vuelve a validar al ejecutar la operación.
 */
export function isEmptyMainBirthDraft(detail: {main?: boolean | null; status?: string | null; dni?: string | null} | null | undefined): boolean
{
    if(!detail || detail.main !== true)
    {
        return false;
    }
    return isMainNewDraft(detail.status) && String(detail.dni ?? "").trim() === "";
}

/** Aviso de éxito tras convertir un interviniente en el principal de la cuenta. */
export function buildMainPromotionSuccessMessage(fullName: string | null | undefined): string
{
    const name = String(fullName ?? "").trim();
    return name !== ""
        ? `${name} es ahora el interviniente principal de tu cuenta.`
        : "El interviniente es ahora el principal de tu cuenta.";
}

/** Cotitular / menor / empresa: solo cuando el Main ya no es el borrador NEW del pago, o no hay Main. */
export function canAddAdditionalIntervener(main: MainUserIdentityDto | null): boolean
{
    if(!main)
    {
        return true;
    }
    return !isMainNewDraft(main.status);
}